import { mergeQueryString, ParamOptions } from 'bernie-core';

import {
  SortAndFiltersCustomEventTypes,
  SortAndFiltersParamsKeys,
  SortAndFiltersRangeParamKey,
} from '@shared-ui/retail-sort-and-filter-lodging';
import { ParamsKeys } from '@shared-ui/search-tools-lodging';

import { parse } from 'query-string';

export interface Categories {
  destination: any;
  criteria: any;
  shoppingContext: {
    multiItem:
      | {
          id: string;
          packageType: string | undefined;
        }
      | undefined;
  };
}

interface UrlQueryStringBuilder {
  name: string;
  value: any;
  existingUrlQueryString: string;
  event: React.ChangeEvent<HTMLInputElement>;
}

const universalSortFilterKeys = [...Object.values(SortAndFiltersParamsKeys), ...Object.values(SortAndFiltersRangeParamKey)];
const universalSortFilterPriceKey = Object.values(SortAndFiltersRangeParamKey);

export const mergeQueryKey = (key: string) => (name: string) => !key.startsWith(name);
// keeps keys that are not universal sort and filter keys
const keepNonUniversalSortFilterKeys = (key: string) =>
  !universalSortFilterKeys.includes(key as SortAndFiltersParamsKeys | SortAndFiltersRangeParamKey);

// keeps keys other than universal sort and filter price key
const shouldRemoveUniversalSortFiltersPriceKey = (key: string) => !universalSortFilterPriceKey.includes(key as SortAndFiltersRangeParamKey);

const unboundedPriceRangeValue = -1;

export const buildUrlQueryString = ({ name, value, existingUrlQueryString, event }: UrlQueryStringBuilder): string => {
  let resultingUrlQueryString;
  let newUrlQueryParameters;
  const eventTargetType = event?.target?.type;
  const eventTargetChecked = event?.target?.checked;
  const parsedUrlQueryString = parse(existingUrlQueryString);

  switch (name) {
    case SortAndFiltersCustomEventTypes.CLEAR:
      // takes the existing query string and filters out USF filters
      resultingUrlQueryString = mergeQueryString(existingUrlQueryString, {}, keepNonUniversalSortFilterKeys);

      return resultingUrlQueryString;

    case SortAndFiltersCustomEventTypes.CLEAR_QUICK_FILTERS: {
      // takes the existing query string and filters out USF filters
      const params = new URLSearchParams(existingUrlQueryString);
      value.forEach((field: { key: string; value: string }) => {
        if (field.value === undefined) {
          params.delete(field.key);
        } else {
          const remainingKeyValues = params.getAll(field.key).filter(keyValue => keyValue !== field.value);
          params.delete(field.key);
          for (const remainingKeyValue of remainingKeyValues) params.append(field.key, remainingKeyValue);
        }
      });
      resultingUrlQueryString = params.toString();

      return resultingUrlQueryString;
    }

    case SortAndFiltersCustomEventTypes.RESTORE:
      return '';

    case SortAndFiltersParamsKeys.PROPERTY_NAME:
      newUrlQueryParameters = { [SortAndFiltersParamsKeys.HOTEL_NAME]: value.hotelName, [ParamsKeys.SELECTED]: value.selected };
      resultingUrlQueryString = mergeQueryString(existingUrlQueryString, newUrlQueryParameters);

      return resultingUrlQueryString;

    case ParamsKeys.REGION_ID:
      newUrlQueryParameters = { [name]: value, [SortAndFiltersParamsKeys.NEIGHBORHOOD]: value };
      resultingUrlQueryString = mergeQueryString(existingUrlQueryString, newUrlQueryParameters);

      return resultingUrlQueryString;

    case SortAndFiltersRangeParamKey.NIGHTLY_PRICE:
    case SortAndFiltersRangeParamKey.TOTAL_PRICE: {
      if (value.min === unboundedPriceRangeValue && value.max === unboundedPriceRangeValue) {
        // remove total price param from existingUrlQueryString
        newUrlQueryParameters = { [name]: undefined };
        resultingUrlQueryString = mergeQueryString(existingUrlQueryString, newUrlQueryParameters);

        return resultingUrlQueryString;
      }
      newUrlQueryParameters = { [name]: [value.min, value.max] } as unknown as ParamOptions; // mergeQueryString's ParamOptions interface should allow string[] for the param value
      resultingUrlQueryString = mergeQueryString(existingUrlQueryString, newUrlQueryParameters, mergeQueryKey(name));

      return resultingUrlQueryString;
    }
    case SortAndFiltersRangeParamKey.PRICE: {
      if (value.min === unboundedPriceRangeValue && value.max === unboundedPriceRangeValue) {
        resultingUrlQueryString = mergeQueryString(existingUrlQueryString, {}, shouldRemoveUniversalSortFiltersPriceKey);

        return resultingUrlQueryString;
      }

      newUrlQueryParameters = { [name]: [value.min, value.max] } as unknown as ParamOptions; // mergeQueryString's ParamOptions interface should allow string[] for the param value
      resultingUrlQueryString = mergeQueryString(existingUrlQueryString, newUrlQueryParameters, mergeQueryKey(name));

      return resultingUrlQueryString;
    }

    default: {
      // checkbox specific logic to support multi selection
      if (eventTargetType === 'checkbox') {
        // deselection (if "value" for given "name" found in the existing URL)
        let currentValue = parsedUrlQueryString[name] ?? [];
        if (typeof currentValue === 'string') currentValue = [currentValue];
        if (!eventTargetChecked) {
          currentValue = Array.isArray(currentValue) ? currentValue.filter(current => current !== value) : [];
          newUrlQueryParameters = { [name]: currentValue };
          resultingUrlQueryString = mergeQueryString(existingUrlQueryString, newUrlQueryParameters);

          return resultingUrlQueryString;
        }

        // selection
        resultingUrlQueryString = mergeQueryString(existingUrlQueryString, {});
        resultingUrlQueryString = resultingUrlQueryString.concat(`&${name}=${value}`);

        return resultingUrlQueryString;
      }

      // everything else (i.e. a radio, stepper input)
      newUrlQueryParameters = { [name]: value };
      resultingUrlQueryString = mergeQueryString(existingUrlQueryString, newUrlQueryParameters);

      return resultingUrlQueryString;
    }
  }
};
